import { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import Layouts from "../../Layouts";
import BomPageTitle from "./BomPageTitle";
import BomPageQuoteTable from "./BomPageQuoteTable";
import BomPageBomDetails from "./BomPageBomDetails";
import BomIssueReportModal from "../Home/BomIssueReportModal";
import { getFile, getBomDetails, getDigiKeyBomEnrichment } from "../../../actions";
import {
  applyImportDigiKeyColumns,
  applyObsoleteAlternateFromExcelLine,
  applyUnmatchedAlternateFromExcelLine,
  getFreightTotalFromDetails,
  getTariffTotalFromDetails,
  isBlankBomLine,
  organizeBomByMountingType,
  parseExcelRowsToBomlines,
} from "../../../constants/digiKeyColumns";
import { applySavedAttritionToBomlines } from "../../../constants/quoteQuantities";
import { normalizeIssueReport } from "../../../constants/issueReportUtils";
import { BOM_ISSUE_REPORT } from "../../../constants/urls";
import { connect } from "react-redux";
import "./bomPageStyle.css";

const BomPage = (props) => {
  const [bomDetails, setBomDetails] = useState([]);
  const [bomLines, setBomLines] = useState();
  const [structuredBomlines, setStructuredBomlines] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [showIssueReportModal, setShowIssueReportModal] = useState(false);
  const [issueReport, setIssueReport] = useState(null);
  const [freightTotal, setFreightTotal] = useState(0);
  const [tariffTotal, setTariffTotal] = useState(0);

  const { match, user, getBomDetails, getFile, getDigiKeyBomEnrichment } = props;

  useEffect(() => {
    const getBomFile = async () => {
      setTableLoading(true);
      setStructuredBomlines(null);

      const bomId = match.params.id;
      const newBomDetails = await getBomDetails(
        user.token,
        bomId
      );
      setBomDetails(newBomDetails);
      setIssueReport(normalizeIssueReport(newBomDetails?.data?.issue_report));
      setFreightTotal(
        getFreightTotalFromDetails(newBomDetails?.data?.freight_details)
      );
      setTariffTotal(
        getTariffTotalFromDetails(newBomDetails?.data?.tariff_details)
      );

      let bomFilePath = newBomDetails?.data?.result_file_path
        ? newBomDetails?.data?.result_file_path
        : newBomDetails?.data?.file_path;

      if (!bomFilePath) {
        setTableLoading(false);
        return;
      }

      const bomFile = await getFile(user.token, bomFilePath);
      if (!bomFile) {
        setTableLoading(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(bomFile);
      const newBomLines = workbook._worksheets.find(
        (worksheet) => worksheet?._rows
      )._rows;
      setBomLines(newBomLines);

      let parsed = parseExcelRowsToBomlines(newBomLines)
        .map((line, index) => ({
          ...applyUnmatchedAlternateFromExcelLine(
            applyObsoleteAlternateFromExcelLine(line)
          ),
          _bomLineIndex: index,
        }))
        .filter((line) => !isBlankBomLine(line));
      const enrichResponse = await getDigiKeyBomEnrichment(
        user.token,
        bomId
      );

      if (
        enrichResponse?.code >= 200 &&
        enrichResponse?.code < 400 &&
        enrichResponse?.data
      ) {
        parsed = applyImportDigiKeyColumns(parsed, enrichResponse.data);
      } else {
        parsed = organizeBomByMountingType(parsed);
      }

      let lineAttrition = newBomDetails?.data?.line_attrition_quantities;
      if (typeof lineAttrition === "string") {
        try {
          lineAttrition = JSON.parse(lineAttrition);
        } catch (e) {
          lineAttrition = [];
        }
      }

      const quotes = newBomDetails?.data?.quote || [];
      if (Array.isArray(lineAttrition) && lineAttrition.length && quotes.length) {
        parsed = applySavedAttritionToBomlines(parsed, quotes, lineAttrition);
      }

      setStructuredBomlines(parsed);
      setTableLoading(false);
    };

    getBomFile();
  }, [match.params.id, user.token, getBomDetails, getFile, getDigiKeyBomEnrichment]);

  const openIssueReport = async () => {
    setShowIssueReportModal(true);
    const bomId = match.params.id;
    if (!bomId || !user.token) {
      return;
    }
    try {
      const res = await fetch(BOM_ISSUE_REPORT(bomId), {
        method: "POST",
        headers: {
          authorization: "Bearer " + user.token,
        },
      });
      const body = await res.json();
      if (body && body.code >= 200 && body.code < 400 && body.data) {
        setIssueReport(normalizeIssueReport(body.data));
      }
    } catch (e) {
      // Keep the stored snapshot if refresh fails.
    }
  };

  return (
    <Layouts>
      <div className="p-20 bomPageBox">
        <BomPageTitle
          bomDetails={bomDetails}
          issueReport={issueReport}
          onOpenIssueReport={openIssueReport}
        />
      </div>
      <BomPageQuoteTable bomDetails={bomDetails}></BomPageQuoteTable>
      <BomPageBomDetails
        bomDetails={bomDetails}
        bomLines={bomLines}
        structuredBomlines={structuredBomlines}
        tableLoading={tableLoading}
        freightTotal={freightTotal}
        tariffTotal={tariffTotal}
      ></BomPageBomDetails>
      <BomIssueReportModal
        isOpen={showIssueReportModal}
        toggle={() => setShowIssueReportModal(false)}
        issueReport={issueReport}
        bomName={bomDetails?.data?.file_name}
      />
    </Layouts>
  );
};

const mapStateToProps = (state) => ({ user: state.user });
export default connect(mapStateToProps, {
  getFile,
  getBomDetails,
  getDigiKeyBomEnrichment,
})(BomPage);
